import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Shield,
  ShieldCheck,
  ShieldOff,
  Trash2,
  AlertCircle,
} from "lucide-react";
import { Button } from "@shared/components/base/Button";
import { authService } from "@shared/core/services/AuthService";
import { TYPOGRAPHY, THEME } from "@shared/constants/designSystem";
import { cn } from "@shared/utils";

/**
 * MFAStatus Component
 *
 * Displays current MFA status and provides management options:
 * - Shows enabled/disabled status
 * - Lists enrolled authenticator apps
 * - Provides option to disable MFA
 * - Triggers enrollment flow
 */
export function MFAStatus({ onEnroll, onUnenroll }) {
  const [mfaFactors, setMfaFactors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showUnenrollConfirm, setShowUnenrollConfirm] = useState(false);
  const [isUnenrolling, setIsUnenrolling] = useState(false);

  const isMFAEnabled = mfaFactors.some((f) => f.status === "verified");

  useEffect(() => {
    loadMFAFactors();
  }, []);

  const loadMFAFactors = async () => {
    setIsLoading(true);
    setError("");

    try {
      const factors = await authService.listMFAFactors();
      setMfaFactors(factors);
    } catch (err) {
      setError("Failed to load MFA status");
      console.error("Error loading MFA factors:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnenroll = async () => {
    if (!mfaFactors.length) return;

    setIsUnenrolling(true);
    setError("");

    try {
      // Unenroll all verified factors
      for (const factor of mfaFactors.filter((f) => f.status === "verified")) {
        await authService.unenrollMFA(factor.id);
      }

      // Reload factors to update UI
      await loadMFAFactors();
      setShowUnenrollConfirm(false);

      // Call callback if provided
      if (onUnenroll) onUnenroll();
    } catch (err) {
      setError(err.message || "Failed to disable MFA");
    } finally {
      setIsUnenrolling(false);
    }
  };

  const handleEnroll = () => {
    if (onEnroll) onEnroll();
  };

  if (isLoading) {
    return (
      <div className={`${THEME.COLORS.BG.DEFAULT} rounded-lg p-6`}>
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-gray-400 animate-pulse" />
          <p
            className={cn(TYPOGRAPHY.BODY, "text-gray-600 dark:text-gray-400")}
          >
            Loading MFA status...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${THEME.COLORS.BG.DEFAULT} rounded-lg p-6 space-y-6`}>
      {/* Status Badge */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div
            className={`${isMFAEnabled ? "bg-green-100 dark:bg-green-900/30" : "bg-gray-100 dark:bg-gray-800"} rounded-full p-2`}
          >
            {isMFAEnabled ? (
              <ShieldCheck className="w-6 h-6 text-green-600 dark:text-green-400" />
            ) : (
              <ShieldOff className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            )}
          </div>
          <div>
            <h4
              className={cn(
                TYPOGRAPHY.HEADING.H4,
                "mb-1 text-gray-900 dark:text-gray-100",
              )}
            >
              Two-Factor Authentication
            </h4>
            <p
              className={cn(
                TYPOGRAPHY.BODY,
                isMFAEnabled
                  ? "text-green-600 dark:text-green-400"
                  : "text-gray-600 dark:text-gray-400",
              )}
            >
              {isMFAEnabled ? "Enabled" : "Not enabled"}
            </p>
          </div>
        </div>
      </div>

      {/* Description */}
      <p className={cn(TYPOGRAPHY.BODY, "text-gray-600 dark:text-gray-400")}>
        {isMFAEnabled
          ? "Your account is protected with two-factor authentication. You'll need your authenticator app to sign in."
          : "Add an extra layer of security by requiring a verification code from your authenticator app when signing in."}
      </p>

      {/* Enrolled Factors List */}
      {isMFAEnabled && mfaFactors.length > 0 && (
        <div className="bg-primary-100 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 p-4 rounded-lg space-y-2">
          {mfaFactors
            .filter((f) => f.status === "verified")
            .map((factor) => (
              <div
                key={factor.id}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <Shield className="w-4 h-4 text-primary-700 dark:text-primary-300" />
                  <div>
                    <p
                      className={cn(
                        TYPOGRAPHY.LABEL,
                        "text-gray-900 dark:text-gray-100",
                      )}
                    >
                      {factor.friendly_name}
                    </p>
                    <p
                      className={cn(
                        TYPOGRAPHY.CAPTION,
                        "text-gray-700 dark:text-gray-300",
                      )}
                    >
                      Added {new Date(factor.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className={cn(TYPOGRAPHY.BODY, "text-red-800 dark:text-red-200")}>
            {error}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        {!isMFAEnabled ? (
          <Button onClick={handleEnroll} className="w-full">
            <Shield className="w-4 h-4 mr-2" />
            Enable Two-Factor Authentication
          </Button>
        ) : showUnenrollConfirm ? (
          <>
            <Button
              variant="primary"
              onClick={handleUnenroll}
              disabled={isUnenrolling}
              className="flex-1 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 focus:ring-red-500"
            >
              {isUnenrolling ? "Disabling..." : "Confirm Disable"}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowUnenrollConfirm(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button
            variant="outline"
            onClick={() => setShowUnenrollConfirm(true)}
            className="w-full"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Disable Two-Factor Authentication
          </Button>
        )}
      </div>

      {/* Warning when disabling */}
      {showUnenrollConfirm && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p
              className={cn(
                TYPOGRAPHY.LABEL,
                "text-amber-800 dark:text-amber-200 mb-1",
              )}
            >
              Are you sure?
            </p>
            <p
              className={cn(
                TYPOGRAPHY.CAPTION,
                "text-amber-700 dark:text-amber-300",
              )}
            >
              Disabling two-factor authentication will make your account less
              secure. You&apos;ll need to re-enroll if you want to enable it
              again.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

MFAStatus.propTypes = {
  onEnroll: PropTypes.func,
  onUnenroll: PropTypes.func,
};
